import api from './api';

export type TrafficMode = 'autopilot' | 'approval';
export type TrafficGoal = 'conversions' | 'leads' | 'sales' | 'traffic' | 'awareness';
export type ActionStatus = 'executed' | 'failed' | 'blocked' | 'proposed' | 'approved' | 'rejected' | 'expired';
export type Verdict = 'melhorou' | 'piorou' | 'neutro' | 'inconclusivo' | 'maturando';

/** O que o responsável conta ao robô sobre o negócio (vai no prompt). */
export interface BusinessResult { id?: string; period: string; campaign?: string | null; leads?: number | null; sales?: number | null; revenue?: number | null; note?: string | null }
export interface BusinessData {
  offer?: string; differentiators?: string; avoid?: string; ideal_customer?: string;
  avg_ticket?: string; customer_value?: string; max_cpl?: string; close_rate?: string;
  sales_cycle?: string; seasonality?: string; competitors?: string; notes?: string;
  results?: BusinessResult[];
}
export interface Material {
  id: string; kind: 'image' | 'video' | 'text'; name: string; url: string | null; content?: string | null;
  notes: string | null; product: string | null; meta: { width?: number; height?: number; duration?: number; size?: number; mime?: string };
  usage: { campaign_id: string; campaign_name?: string; platform: string; external_id?: string; at: string }[];
  status: 'active' | 'archived'; created_at: string;
}

export interface ProfileLocation {
  name: string;
  canonicalName?: string;
  id?: string;
  targetType?: string;
  countryCode?: string;
}

/** Público do produto: o robô nunca segmenta fora disto. */
export interface TargetingProfile {
  locations: ProfileLocation[];
  excluded_locations: ProfileLocation[];
  age_min: number | null;
  age_max: number | null;
  genders: ('all' | 'male' | 'female')[];
  devices: ('mobile' | 'desktop' | 'tablet')[];
}

export interface Guardrails {
  max_increase_pct: number;
  max_decrease_pct: number;
  min_daily_budget: number;
  pace_tolerance_pct: number;
  allow_pause_campaigns: boolean;
  allow_resume_campaigns: boolean;
  allow_pause_ads: boolean;
  allow_negative_keywords: boolean;
  allow_keywords: boolean;
  allow_automations: boolean;
  allow_remarketing: boolean;
  allow_ai_max: boolean;
  allow_targeting: boolean;
  allow_new_ads: boolean;
  excluded_campaign_ids: string[];
  include_new_campaigns: boolean;
  scope_since?: string;
  targeting_profile?: TargetingProfile;
  allow_autopilot_new_campaigns: boolean;
  pause_on_budget_exhausted: boolean;
  maturation_days: number;
  cooldown_days: number;
}

export interface MetricBlock {
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  cpc: number | null;
  cpa: number | null;
}

export interface SnapshotCampaign {
  id: string;
  nome: string;
  plataformas: string[];
  status: 'active' | 'paused';
  objetivo: string;
  tipo_orcamento: 'daily' | 'lifetime';
  orcamento: number;
  moeda: string;
  orcamento_no_nivel_campanha_meta: boolean;
  ultima_sincronizacao: string | null;
  status_na_plataforma?: string;
  veiculacao_na_plataforma?: string;
  rodando_na_plataforma?: boolean;
  orcamento_real_na_plataforma?: number;
  ultimos_7_dias: MetricBlock;
  ultimos_30_dias: MetricBlock;
  no_mes: MetricBlock;
  hoje: MetricBlock;
}

export interface Pacing {
  hoje: string;
  mes: string;
  dias_no_mes: number;
  dias_restantes: number;
  verba_mensal: number;
  gasto_no_mes: number;
  gasto_hoje: number;
  restante: number;
  teto_diario: number;
  teto_diario_com_folga: number;
  soma_orcamentos_diarios_ativos: number;
  gasto_ideal_ate_ontem: number;
  desvio_do_ritmo: number;
  verba_esgotada: boolean;
}

export interface AutomationRuleInfo {
  id: string;
  nome: string;
  status: string;
  gatilho: string;
  valor_gatilho: number;
  acao: string;
  campanhas: string[];
  todas_as_campanhas: boolean;
  criada_pelo_robo: boolean;
}

export interface Scoreboard {
  melhorou: number;
  piorou: number;
  neutro: number;
  inconclusivo: number;
  maturando: number;
  taxa_acerto: number | null;
}

export interface Snapshot {
  gerado_em: string;
  ritmo: Pacing;
  campanhas: SnapshotCampaign[];
  automacoes?: AutomationRuleInfo[];
  placar?: Scoreboard;
}

export interface WindowMetrics {
  dias: number;
  gasto_dia: number;
  conv_dia: number;
  cliques_dia: number;
  cpa: number | null;
  cpc: number | null;
  ctr: number;
  cliques: number;
  conv: number;
}

export interface Impact {
  at: string;
  dia: string;
  tool: string;
  campaign_id: string;
  campaign_name: string | null;
  plan: Record<string, any>;
  reason: string | null;
  antes: WindowMetrics;
  depois: WindowMetrics;
  veredito: Verdict;
  motivo_veredito: string | null;
  maturacao_dias: number;
  dias_faltando: number;
}

export interface TraceStep {
  n: number;
  at: string;
  tipo: 'retrato' | 'pensamento' | 'leitura' | 'acao' | 'aprendizado';
  ferramenta?: string;
  entrada?: string;
  ok?: boolean;
  resultado?: string;
  texto?: string;
  action_id?: string;
}

export interface Directive {
  id: string;
  text: string;
  campaign_ids: string[];
  until: string | null;
  created_at: string;
}

export interface Learning {
  id: string;
  text: string;
  run_id: string;
  at: string;
}

export interface DayPoint extends MetricBlock { date: string }

export interface Evolution {
  diaria: DayPoint[];
  mes: { date: string; ideal: number; real: number | null }[];
  acoes: { at: string; date: string; tool: string; campaign_id: string | null; campaign_name: string | null; plan: Record<string, any>; reason: string | null }[];
  comparativo: { desde: string; antes: MetricBlock & { dias: number; conversoes_por_dia: number; gasto_por_dia: number }; depois: MetricBlock & { dias: number; conversoes_por_dia: number; gasto_por_dia: number } } | null;
  estatisticas: { acoes: number; por_tipo: Record<string, number>; verba_realocada_por_dia: number; negativas_adicionadas: number; positivas_adicionadas: number };
  series_por_campanha: Record<string, { date: string; spend: number; conversions: number; cpc: number | null; cpa: number | null }[]>;
  impactos: Impact[];
  placar: Scoreboard;
  esperado: Expected | null;
}

export type ExpectedVerdict = 'acima' | 'abaixo' | 'na_media' | 'pouco_volume' | 'sem_base';
type Totals = { spend: number; impressions: number; clicks: number; conversions: number; cpa: number | null; cpc: number | null; ctr: number | null; conv_rate: number | null };

/** O mês contra a média da própria conta (90 dias antes do mês). */
export interface Expected {
  mes: string;
  base: Totals & { de: string; ate: string; dias: number; suficiente: boolean };
  real: Totals & { de: string; ate: string; dias_fechados: number; dias_no_mes: number };
  esperado: number | null;
  margem: number | null;
  indice: number | null;
  veredito: ExpectedVerdict;
  projecao_fim_do_mes: number | null;
  esperado_com_a_verba: number | null;
  meta_com_a_verba: number | null;
  pontos: { date: string; real: number | null; esperado: number | null; projecao: number | null; esperado_verba: number | null; meta: number | null }[];
  por_campanha: { id: string; nome: string; gasto: number; conversoes: number; cpa: number | null; cpa_media: number | null; esperado: number | null; indice: number | null; veredito: ExpectedVerdict }[];
}

export interface ChangeItem extends RunAction {
  run_id: string;
  run_trigger: string;
  run_request: string | null;
  run_at: string;
  impacto: { veredito: Verdict; antes: WindowMetrics; depois: WindowMetrics; dias_faltando?: number; motivo_veredito?: string | null } | null;
}

export interface AdsAlert {
  severity: 'critical' | 'warning' | 'info';
  platform: 'google' | 'meta' | 'linkedin' | 'openai' | 'panel';
  code: string;
  title: string;
  detail: string | null;
  campaign_id?: string | null;
  campaign_name?: string | null;
}

export interface PlatformHealth {
  connected: boolean;
  ok?: boolean;
  account?: { id?: string; name?: string; currency?: string; status?: string | number; campaigns_running?: number };
  alerts: AdsAlert[];
}

export interface AdsHealth {
  checked_at: string;
  platforms: { google: PlatformHealth; meta: PlatformHealth; linkedin: PlatformHealth; openai: PlatformHealth };
  alerts: AdsAlert[];
}

export interface RunAction {
  id: string;
  tool: string;
  input: Record<string, any>;
  reason: string | null;
  campaign_name: string | null;
  status: ActionStatus;
  plan?: { de?: number; para?: number; keywords?: string[] };
  result?: string | null;
  error?: string | null;
  at: string;
  decided_at?: string;
  verification?: { status: 'ok' | 'mismatch' | 'unverifiable' | 'error' | 'pending'; observed?: any; expected?: any; at?: string } | null;
}

export interface TrafficRun {
  id: string;
  manager_id: string;
  trigger: 'cron' | 'manual' | 'chat';
  request?: string | null;
  focus_campaign_ids?: string[] | null;
  trace?: TraceStep[];
  snapshot?: Snapshot;
  status: 'running' | 'completed' | 'failed' | 'skipped';
  mode: TrafficMode;
  summary: string | null;
  actions: RunAction[];
  error: string | null;
  tokens_used: number;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

export interface TrafficManager {
  id: string;
  name: string;
  status: 'active' | 'paused';
  mode: TrafficMode;
  instructions: string | null;
  monthly_budget: number | string;
  currency: string;
  goal: TrafficGoal;
  target_cpa: number | string | null;
  target_roas: number | string | null;
  platforms: string[];
  campaign_ids: string[];
  guardrails: Partial<Guardrails>;
  guardrails_effective: Guardrails;
  times_of_day: string[];
  timezone: string;
  memory: string | null;
  learnings: Learning[];
  directives: Directive[];
  last_run_at: string | null;
  next_run_at: string | null;
  runs_count: number;
  pending_count: number;
  last_run: TrafficRun | null;
  snapshot?: Snapshot;
  metadata?: { language?: string; business?: BusinessData } | null;
}

export interface EligibleCampaign {
  id: string;
  name: string;
  status: string;
  platforms: string[];
  budget_type: string;
  budget_amount: number;
  currency: string;
  running: boolean | null;
  spend_30d: number;
  conversions_30d: number;
  cpa_30d: number | null;
  created_at?: string;
  created_by_manager?: boolean;
  excluded?: boolean;
  managed?: boolean;
}

export type TrafficManagerInput = Partial<Omit<TrafficManager, 'guardrails'>> & { guardrails?: Partial<Guardrails>; language?: string; business?: BusinessData };

const trafficManagerService = {
  async list(): Promise<{ data: TrafficManager[]; defaults: Guardrails }> {
    const r = await api.get('/traffic-managers');
    return { data: r.data.data, defaults: r.data.defaults };
  },
  async get(id: string): Promise<TrafficManager> {
    return (await api.get(`/traffic-managers/${id}`)).data.data;
  },
  async create(data: TrafficManagerInput): Promise<TrafficManager> {
    return (await api.post('/traffic-managers', data)).data.data;
  },
  async update(id: string, data: TrafficManagerInput): Promise<TrafficManager> {
    return (await api.put(`/traffic-managers/${id}`, data)).data.data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/traffic-managers/${id}`);
  },
  async runNow(id: string): Promise<{ run: TrafficRun; already: boolean }> {
    const r = await api.post(`/traffic-managers/${id}/run`);
    return { run: r.data.data, already: !!r.data.already };
  },
  async runs(id: string, limit = 20, kind?: 'chat' | 'routine'): Promise<TrafficRun[]> {
    return (await api.get(`/traffic-managers/${id}/runs`, { params: { limit, kind } })).data.data;
  },
  async getRun(runId: string): Promise<TrafficRun> {
    return (await api.get(`/traffic-managers/runs/${runId}`)).data.data;
  },
  async chat(id: string, message: string, campaignIds?: string[], language?: string): Promise<TrafficRun> {
    return (await api.post(`/traffic-managers/${id}/chat`, { message, language, campaign_ids: campaignIds && campaignIds.length ? campaignIds : undefined })).data.data;
  },
  async evolution(id: string, days = 60): Promise<Evolution> {
    return (await api.get(`/traffic-managers/${id}/evolution`, { params: { days } })).data.data;
  },
  async changes(id: string, params: { campaign_id?: string; status?: string; days?: number } = {}): Promise<{ data: ChangeItem[]; placar: Scoreboard }> {
    const r = await api.get(`/traffic-managers/${id}/changes`, { params });
    return { data: r.data.data, placar: r.data.placar };
  },
  async addDirective(id: string, d: { text: string; campaign_ids?: string[]; until?: string | null }): Promise<TrafficManager> {
    return (await api.post(`/traffic-managers/${id}/directives`, d)).data.data;
  },
  async removeDirective(id: string, directiveId: string): Promise<TrafficManager> {
    return (await api.delete(`/traffic-managers/${id}/directives/${directiveId}`)).data.data;
  },
  async removeLearning(id: string, learningId: string): Promise<TrafficManager> {
    return (await api.delete(`/traffic-managers/${id}/learnings/${learningId}`)).data.data;
  },
  async approve(runId: string, actionId?: string): Promise<RunAction[]> {
    const url = actionId ? `/traffic-managers/runs/${runId}/actions/${actionId}/approve` : `/traffic-managers/runs/${runId}/approve-all`;
    return (await api.post(url)).data.data.actions;
  },
  async reject(runId: string, actionId?: string): Promise<RunAction[]> {
    const url = actionId ? `/traffic-managers/runs/${runId}/actions/${actionId}/reject` : `/traffic-managers/runs/${runId}/reject-all`;
    return (await api.post(url)).data.data.actions;
  },
  async health(fresh = false): Promise<AdsHealth> {
    return (await api.get('/campaigns/ads-health', { params: fresh ? { fresh: '1' } : {} })).data.data;
  },
  async fixStatusMismatch(): Promise<{ fixed: number; health: AdsHealth }> {
    return (await api.post('/campaigns/ads-health/fix-status')).data.data;
  },
  async eligibleCampaigns(managerId?: string): Promise<EligibleCampaign[]> {
    return (await api.get('/traffic-managers/eligible-campaigns', { params: managerId ? { manager_id: managerId } : {} })).data.data;
  },
  async materials(id: string): Promise<Material[]> {
    return (await api.get(`/traffic-managers/${id}/materials`)).data.data;
  },
  async addMaterial(id: string, data: Partial<Material> & { kind: Material['kind'] }): Promise<Material> {
    return (await api.post(`/traffic-managers/${id}/materials`, data)).data.data;
  },
  async updateMaterial(id: string, materialId: string, data: Partial<Material>): Promise<Material> {
    return (await api.put(`/traffic-managers/${id}/materials/${materialId}`, data)).data.data;
  },
  /**
   * Sobe o arquivo e devolve a URL pública. Imagem vai pela API (/media/upload,
   * até 10 MB). Vídeo vai DIRETO para o armazenamento pela URL assinada: o
   * Cloudflare na frente da API corta envios acima de 100 MB.
   */
  async uploadFile(file: File): Promise<{ url: string; size: number }> {
    if (file.type.startsWith('video/')) {
      const p = (await api.post('/media/presigned-url', { filename: file.name, contentType: file.type, fileSize: file.size })).data.data;
      const put = await fetch(p.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!put.ok) throw new Error(`upload ${put.status}`);
      return { url: p.url, size: file.size };
    }
    const fd = new FormData();
    fd.append('file', file);
    const r = await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
    return { url: r.data.data.url, size: r.data.data.fileSize };
  },
  /** Baixa o relatório do mês (PDF ou Excel). */
  async downloadReport(id: string, month: string, format: 'pdf' | 'xlsx', lang: string): Promise<void> {
    const r = await api.get(`/traffic-managers/${id}/report`, { params: { month, format, lang }, responseType: 'blob', timeout: 120000 });
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gestor-de-trafego-${month}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }
};

export default trafficManagerService;
